// @ts-check
import { doc, runTransaction } from "firebase/firestore";

/**
 * Numeración de gastos y pagos (contrato C1 de la Fase 0D).
 *
 * expensesid y paymentid salían de un máximo calculado en el cliente sobre
 * TODA la colección (orderBy desc + limit 1). Con las reglas 0D esa consulta
 * se niega (no fija el edificio): el id quedaba en 1, se repetía, y los fondos
 * (que emparejan pago con gasto por expensesid) se corrompían en silencio.
 *
 * Ahora cada id sale de un contador atómico: counters/expenses_global y
 * counters/payments_global = { seq: entero }. El cliente lo incrementa en una
 * transacción (get → seq + 1 → update) y NUNCA lo crea: lo inicializa el
 * servidor con el máximo actual (script con dry-run). Si el contador falta,
 * el alta se corta con el error `contador_no_inicializado`.
 */

export const CONTADOR_GASTOS = "expenses_global";
export const CONTADOR_PAGOS = "payments_global";
export const CONTADOR_NO_INICIALIZADO = "contador_no_inicializado";

/**
 * @param {string} contador
 * @param {boolean} existe
 */
function errorContador(contador, existe) {
  const mensaje = existe
    ? `El contador counters/${contador} no tiene un seq entero válido.`
    : `Falta inicializar el contador counters/${contador}.`;
  return Object.assign(new Error(mensaje), { code: CONTADOR_NO_INICIALIZADO, contador });
}

/**
 * Reserva el próximo id secuencial del contador. Si se pasa `escribir`, sus
 * escrituras van en la MISMA transacción que el incremento: el alta y el id
 * se confirman juntos o ninguno (sin ids repetidos ni salteados).
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {string} contador - CONTADOR_GASTOS o CONTADOR_PAGOS
 * @param {(tx: import("firebase/firestore").Transaction, id: number) => void} [escribir] - Escrituras del alta, con el id ya reservado
 * @returns {Promise<number>} El id reservado (seq + 1)
 * @throws {Error} code `contador_no_inicializado` si el contador no existe o su seq no es un entero ≥ 0
 */
export async function reservarIdSecuencial(db, contador, escribir) {
  const ref = doc(db, "counters", contador);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const seq = snap.exists() ? (snap.data() || {}).seq : undefined;
    if (typeof seq !== "number" || !Number.isInteger(seq) || seq < 0) {
      throw errorContador(contador, snap.exists());
    }
    const nuevo = seq + 1;
    tx.update(ref, { seq: nuevo });
    if (escribir) escribir(tx, nuevo);
    return nuevo;
  });
}

/**
 * Mensaje para el staff cuando un alta no se pudo numerar. Devuelve null si el
 * error no es del contador (el llamador usa su mensaje genérico).
 *
 * @param {unknown} e - Error atrapado
 * @param {string} [que] - Qué no se guardó ("gasto", "pago", "saldo"…)
 * @returns {string | null}
 */
export function mensajeErrorNumeracion(e, que = "gasto") {
  const err = /** @type {{ code?: string, contador?: string } | null} */ (e && typeof e === "object" ? e : null);
  if (!err || err.code !== CONTADOR_NO_INICIALIZADO) return null;
  const numeracion = err.contador === CONTADOR_PAGOS ? "pagos" : "gastos";
  return `No se guardó el ${que}: falta inicializar la numeración de ${numeracion} (counters/${err.contador}). Avisá a soporte.`;
}
